import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";

/**
 * Insights — a weekly digest of the LOOXS house.
 * Live counters (pre-orders, waitlist, gift-card orders, revenue), a 7-day series bar chart,
 * top chapters by pre-orders, most-waitlisted pieces, and a "send digest email" trigger.
 */
export default function AdminInsights() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [sending, setSending] = useState(false);
  const [emailTo, setEmailTo] = useState("ceo@looxsfashion.com");

  useEffect(() => { api.get(`/admin/insights?days=${days}`).then(r => setData(r.data)); }, [days]);
  if (!data) return <p className="text-ivory/50">Loading insights…</p>;

  const maxSeries = Math.max(1, ...data.series.flatMap(d => [d.preorders, d.waitlist, d.gift_cards]));
  const send = async () => {
    if (!emailTo.trim()) return;
    setSending(true);
    try {
      const r = await api.post("/admin/insights/send-digest", { to: emailTo.trim() });
      if (r.data.ok) toast.success(`Digest sent to ${emailTo}`);
      else toast.error("Digest send failed — check email credentials");
    } catch { toast.error("Digest send failed"); }
    finally { setSending(false); }
  };

  const cards = [
    { label: "Pre-orders",  recent: data.preorders.recent,   total: data.preorders.total,   testid: "insight-preorders" },
    { label: "Waitlist",    recent: data.waitlist.recent,    total: data.waitlist.total,    testid: "insight-waitlist" },
    { label: "Gift cards",  recent: data.gift_orders.recent, total: data.gift_orders.total, testid: "insight-giftcards" },
    { label: "Revenue INR", recent: data.revenue_inr.recent, total: "· gift-card channel",  testid: "insight-revenue", isMoney: true },
  ];

  return (
    <div>
      <p className="text-[10px] tracking-luxury uppercase text-gold mb-4">Weekly digest</p>
      <div className="flex items-end justify-between mb-12">
        <h1 className="font-editorial text-5xl tracking-tight">Insights</h1>
        <div className="flex items-center gap-3">
          <select data-testid="insight-window" value={days} onChange={e => setDays(Number(e.target.value))}
            className="bg-onyx border border-ivory/15 text-ivory text-xs px-3 py-2">
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
          </select>
        </div>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ivory/10 mb-16">
        {cards.map(c => (
          <div key={c.label} data-testid={c.testid} className="bg-onyx p-8">
            <p className="text-[10px] tracking-widest-plus uppercase text-ivory/50">{c.label}</p>
            <p className="font-editorial text-5xl mt-4">{c.isMoney ? `₹ ${c.recent.toLocaleString("en-IN")}` : c.recent}</p>
            <p className="text-[10px] tracking-widest-plus uppercase text-ivory/40 mt-2">
              {typeof c.total === "number" ? `${c.total} lifetime` : c.total}
            </p>
          </div>
        ))}
      </div>

      {/* Series chart */}
      <div className="mb-16">
        <p className="text-[11px] tracking-widest-plus uppercase text-gold mb-6">Daily activity — {data.window_days} days</p>
        <div className="bg-onyx p-8">
          <div className="flex items-end justify-between gap-2 h-56" data-testid="insight-series">
            {data.series.map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full flex items-end justify-center gap-1 h-full">
                  <div className="w-2 bg-gold" style={{ height: `${(d.preorders / maxSeries) * 100}%` }} title={`${d.preorders} preorders`} />
                  <div className="w-2 bg-ivory/50" style={{ height: `${(d.waitlist / maxSeries) * 100}%` }} title={`${d.waitlist} waitlist`} />
                  <div className="w-2 bg-goldDark" style={{ height: `${(d.gift_cards / maxSeries) * 100}%` }} title={`${d.gift_cards} gift cards`} />
                </div>
                <p className="text-[9px] tracking-widest-plus uppercase text-ivory/40 mt-2">{d.date}</p>
              </div>
            ))}
          </div>
          <div className="flex gap-6 mt-6 text-[10px] tracking-widest-plus uppercase text-ivory/60">
            <span className="flex items-center gap-2"><span className="w-3 h-3 bg-gold inline-block" /> Pre-orders</span>
            <span className="flex items-center gap-2"><span className="w-3 h-3 bg-ivory/50 inline-block" /> Waitlist</span>
            <span className="flex items-center gap-2"><span className="w-3 h-3 bg-goldDark inline-block" /> Gift cards</span>
          </div>
        </div>
      </div>

      {/* Top chapters + waitlist */}
      <div className="grid md:grid-cols-2 gap-8 mb-16">
        <div className="bg-onyx p-8">
          <p className="text-[11px] tracking-widest-plus uppercase text-gold mb-4">Top chapters — pre-orders</p>
          <div data-testid="insight-top-chapters">
            {data.top_chapters.length ? data.top_chapters.map(c => (
              <div key={c.chapter} className="flex items-baseline justify-between py-2 border-b border-ivory/10 last:border-0">
                <span className="font-editorial text-lg">Chapter {c.chapter}</span>
                <span className="text-gold font-editorial text-2xl">{c.count}</span>
              </div>
            )) : <p className="text-ivory/40 italic text-sm">No pre-orders in this window.</p>}
          </div>
        </div>
        <div className="bg-onyx p-8">
          <p className="text-[11px] tracking-widest-plus uppercase text-gold mb-4">Most waitlisted pieces</p>
          <div data-testid="insight-top-waitlist">
            {data.top_waitlist.length ? data.top_waitlist.map(w => (
              <div key={w.slug} className="flex items-baseline justify-between py-2 border-b border-ivory/10 last:border-0">
                <span className="text-sm">{w.name}</span>
                <span className="text-gold font-editorial text-2xl">{w.count}</span>
              </div>
            )) : <p className="text-ivory/40 italic text-sm">No waitlist signups in this window.</p>}
          </div>
        </div>
      </div>

      {/* Send digest */}
      <div className="bg-onyx p-8">
        <p className="text-[11px] tracking-widest-plus uppercase text-gold mb-4">Send weekly digest now</p>
        <p className="text-ivory/60 text-sm mb-4">
          A full weekly digest is automatically sent to the founder every Monday at 09:00 UTC.
          Trigger an on-demand email below.
        </p>
        <div className="flex gap-3">
          <input
            data-testid="insight-digest-email"
            value={emailTo}
            onChange={e => setEmailTo(e.target.value)}
            className="flex-1 bg-onyx border border-ivory/15 text-ivory p-3 text-sm"
            placeholder="founder@looxsfashion.com"
          />
          <button
            data-testid="insight-digest-send"
            disabled={sending}
            onClick={send}
            className="bg-gold text-ink px-6 py-3 text-[11px] tracking-widest-plus uppercase flex items-center gap-2 disabled:opacity-40"
          >
            {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send digest</>}
          </button>
        </div>
      </div>
    </div>
  );
}
